/**
 * Штатные 4xx-состояния для экранов раздела «Планирование сезона» (AK-541 +
 * P1-2 от head-web). Две карточки, обе используются и на вкладке настроек
 * `/company/season`, и на списке сезонов `/planning`. Классификация — не
 * здесь; сюда приходят уже развётвлённые случаи из `classifySeasonConfigError`.
 *
 * Тексты параметризованы: одна карточка «Сначала создайте компанию» звучит
 * по-разному на настройках («сможете настроить сезон») и на списке сезонов
 * («сможете открыть планирование»). Никакой бизнес-логики — чистый рендер.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';

/** Минимальный контракт для селектора компаний — берём из GET /companies. */
export interface CompanyPick {
  id: string;
  name: string;
  short_name?: string | null;
}

/**
 * 404 — в тенанте нет компаний. Ведём туда, где компанию реально заводят, не
 * в тупик «Обновите страницу».
 *
 * Ведём на вкладку «Профиль», а не на «Структуру», как было раньше:
 * запись компании создаёт форма реквизитов (`CompanyProfileTab`), а на
 * «Структуре» без компании кнопки создания хозяйства и кластера ничего не
 * делают. Прежняя ссылка отправляла человека с пустого экрана на другой
 * пустой экран.
 *
 * `hint` — окончание фразы: «…и сможете <hint>».
 */
export function NoCompanyState({ hint }: { hint: string }) {
  return (
    <Card className="mt-4">
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">Сначала создайте компанию</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <Stack gap={3}>
          <p className="text-sm text-muted-foreground">
            {`Раздел «Планирование сезона» работает для компании. Реквизиты
            компании ещё не заполнены — заполните их на вкладке «Профиль», потом
            вернитесь сюда, и ${hint}.`}
          </p>
          <div>
            <Button asChild>
              <Link to="/company/$tab" params={{ tab: 'profile' }}>
                <Building2 className="mr-2 h-4 w-4" />
                Заполнить реквизиты компании
              </Link>
            </Button>
          </div>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * 409 `company_ambiguous` — в холдинге больше одной компании, бэк не решает
 * молча. Локальный `Select`, после выбора родитель ретраит запрос с
 * `?company_id=`. Список компаний тянем прямо тут — общий стор ради двух
 * экранов не строим (узкий диф, вынесем после вехи).
 */
export function PickCompanyState({
  onPick,
  title,
  hint,
}: {
  onPick: (id: string) => void;
  title: string;
  hint: string;
}) {
  const companies = useQuery({
    queryKey: ['planning', 'companies-for-pick'],
    queryFn: () => api.get<CompanyPick[]>('/api/v1/companies'),
    staleTime: 60_000,
  });
  return (
    <Card className="mt-4">
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <Stack gap={3}>
          <p className="text-sm text-muted-foreground">
            {`У вас в холдинге несколько компаний. ${hint}`}
          </p>
          {companies.isLoading ? (
            <Skeleton className="h-10 w-full max-w-sm" />
          ) : companies.isError || !companies.data ? (
            <p className="text-sm text-destructive">
              Не удалось загрузить список компаний. Обновите страницу.
            </p>
          ) : (
            <Select onValueChange={(v) => onPick(v)}>
              <SelectTrigger className="max-w-sm">
                <SelectValue placeholder="Выберите компанию" />
              </SelectTrigger>
              <SelectContent>
                {companies.data.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.short_name?.trim() || c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
